/**
 * O que faz: Centraliza as cores e rótulos de cada status de agendamento,
 * para que cards, badges e timelines usem sempre a mesma paleta.
 * Onde usa: CalendarPage, AgendamentoCard, TimelineDayView, AgendamentoTimelineCard.
 * Conexões: Lê o enum e as opções de status de '@/constants/agendamentos'.
 * Tabelas: Nenhuma (apenas apresentação, sem acesso ao banco).
 * Autor: IA (Kilo)
 * Data: 22/07/2026
 */
import { AgendamentoStatus, AGENDAMENTO_STATUS_OPTIONS } from '@/constants/agendamentos';

/**
 * Conjunto de classes Tailwind que representam visualmente um status.
 * Manter o formato `border-{cor}-500` no campo `border` é obrigatório:
 * o card da timeline converte essa classe em fundo via string replace.
 */
export interface AgendamentoStatusClasses {
  border: string;   // Borda de destaque lateral do card (ex: border-l-4)
  tagBg: string;    // Fundo suave da badge de status
  tagText: string;  // Cor do texto da badge de status
  dot: string;      // Bolinha colorida exibida dentro da badge
  softBg: string;   // Fundo suave do avatar com as iniciais do cliente
}

// Mapeia cada status para sua identidade visual, evitando switches duplicados
// espalhados pela página e garantindo que uma nova cor seja trocada em um só lugar.
export const getStatusClasses = (status: string): AgendamentoStatusClasses => {
  switch (status) {
    case AgendamentoStatus.CONFIRMADO:
      return {
        border: 'border-green-500',
        tagBg: 'bg-green-100',
        tagText: 'text-green-700',
        dot: 'bg-green-500',
        softBg: 'bg-green-50',
      };
    case AgendamentoStatus.AGENDADO:
      return {
        border: 'border-blue-500',
        tagBg: 'bg-blue-100',
        tagText: 'text-blue-700',
        dot: 'bg-blue-500',
        softBg: 'bg-blue-50',
      };
    case AgendamentoStatus.REALIZADO:
      return {
        border: 'border-purple-500',
        tagBg: 'bg-purple-100',
        tagText: 'text-purple-700',
        dot: 'bg-purple-500',
        softBg: 'bg-purple-50',
      };
    case AgendamentoStatus.PAGO:
      return {
        border: 'border-emerald-500',
        tagBg: 'bg-emerald-100',
        tagText: 'text-emerald-700',
        dot: 'bg-emerald-500',
        softBg: 'bg-emerald-50',
      };
    case AgendamentoStatus.CANCELADO:
      return {
        border: 'border-red-500',
        tagBg: 'bg-red-100',
        tagText: 'text-red-700',
        dot: 'bg-red-500',
        softBg: 'bg-red-50',
      };
    case AgendamentoStatus.NAO_COMPARECEU:
      return {
        border: 'border-yellow-500',
        tagBg: 'bg-yellow-100',
        tagText: 'text-yellow-700',
        dot: 'bg-yellow-500',
        softBg: 'bg-yellow-50',
      };
    default:
      // Fallback neutro: cobre valores inesperados vindos do banco sem quebrar a UI.
      return {
        border: 'border-gray-300',
        tagBg: 'bg-gray-100',
        tagText: 'text-gray-700',
        dot: 'bg-gray-400',
        softBg: 'bg-gray-50',
      };
  }
};

// Busca o rótulo amigável na lista central de opções; se não encontrar,
// exibe o valor cru para facilitar o diagnóstico de dados inconsistentes.
export const getStatusText = (status: string): string => {
  const statusOption = AGENDAMENTO_STATUS_OPTIONS.find(opt => opt.value === status);
  return statusOption?.label || status;
};
